import React, { PropsWithChildren, useEffect, useMemo } from "react";
import { useResearchMessages } from "../../../adapters/deep-research/state/hooks/stateHooks.ts";
import { MessageBubble } from "../../../feature-components/LegacyChatMessages/MessageBubble.tsx";
import { motion } from "motion/react";
import markdownStyles from "../../../ui-library/MarkdownContent.module.css";
import Markdown from "react-markdown";
import { LoadingDots } from "../../../ui-library/LoadingDots/LoadingDots.tsx";
import remarkGfm from "remark-gfm";
import { ExecutionDetails } from "../../../feature-components/ExecutionDetails/ExecutionDetails.tsx";
import type {
  Message,
  ResearchId,
} from "../../../adapters/deep-research/state/AppState.ts";
import { useFeatureFlagValue } from "../../../config/FeatureFlagManager/hooks";
import { MessageContext } from "../../../feature-components/LegacyChatMessages/MessageContext.tsx";
import { useDeepResearchService } from "../../../adapters/deep-research/state/hooks/useDeepResearchService.tsx";
import styles from "./ConnectedChatMessages.module.css";
import {
  CitationComponent,
  MarkdownWithCitations,
} from "../../../feature-components/MarkdownWithCitations/MarkdownWithCitations.tsx";
import { Citation } from "../../../feature-components/MarkdownWithCitations/Citation.tsx";
import { stripCiteTags } from "../../../feature-components/MarkdownWithCitations/lib/stripCiteTags.ts";
import { FloatingCitation } from "../../../feature-components/FloatingCitation/FloatingCitation.tsx";

function mergeThoughtMessages(messages: Message[]): Message[] {
  const result: Message[] = [];
  let accumulatedThoughts = "";

  for (const message of messages) {
    const isThought =
      message.thought === true || message.thought === ("true" as unknown);

    if (isThought) {
      accumulatedThoughts +=
        (accumulatedThoughts ? "\n\n" : "") + (message.value || "");
    } else {
      if (message.sender === "agent" && accumulatedThoughts) {
        result.push({
          ...message,
          thoughts:
            accumulatedThoughts +
            (message.thoughts ? "\n\n" + message.thoughts : ""),
        });
        accumulatedThoughts = "";
      } else {
        result.push(message);
      }
    }
  }

  if (accumulatedThoughts) {
    const lastAgentIndex = result.findLastIndex((m) => m.sender === "agent");
    if (lastAgentIndex >= 0) {
      result[lastAgentIndex] = {
        ...result[lastAgentIndex],
        thoughts:
          (result[lastAgentIndex].thoughts
            ? result[lastAgentIndex].thoughts + "\n\n"
            : "") + accumulatedThoughts,
      };
    } else {
      result.push({
        id: "accumulated-thoughts-" + Date.now(),
        value: "",
        thoughts: accumulatedThoughts,
        sender: "agent",
        create_time: new Date().toISOString(),
        status: "completed",
      });
    }
  }

  return result;
}

export const ConnectedChatMessages: React.FC<{ researchId: ResearchId }> = (
  props,
) => {
  const { messages, loading } = useResearchMessages(props.researchId);
  const limitedHeightExecutionDetailsFlag = useFeatureFlagValue(
    "enableLimitedHeightExecutionDetails",
  );
  const enableNumberedCitations = useFeatureFlagValue(
    "enableNumberedCitations",
  );

  const containerRef = React.useRef<HTMLDivElement>(null);

  const processedMessages = React.useMemo(
    () => mergeThoughtMessages(messages),
    [messages],
  );

  const lastMessage = processedMessages[processedMessages.length - 1];
  const isStreaming =
    lastMessage?.status === "optimistic" ||
    lastMessage?.status === "processing";

  useEffect(() => {
    containerRef.current?.scrollTo({
      top: containerRef.current.scrollHeight,
      behavior: isStreaming ? "instant" : "smooth",
    });
  }, [
    processedMessages.length,
    lastMessage?.value,
    lastMessage?.thoughts,
    isStreaming,
  ]);

  return (
    <motion.div
      ref={containerRef}
      data-chat-messages-container
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        overflowY: "auto",
        padding: "8px",
        boxSizing: "border-box",
        flexGrow: 1,
      }}
      initial={{ flexGrow: 0 }}
      animate={{ flexGrow: 1 }}
      transition={{ duration: 0.1, ease: "easeInOut" }}
    >
      {loading && processedMessages.length === 0 && (
        <div>Loading messages...</div>
      )}
      {processedMessages.map((message) => (
        <MessageItem
          key={message.id}
          message={message}
          flags={{
            limitedHeightExecutionDetailsFlag,
            enableNumberedCitations,
          }}
          researchId={props.researchId}
        />
      ))}
    </motion.div>
  );
};

const MessageItem = (props: {
  message: Message;
  flags: {
    limitedHeightExecutionDetailsFlag: boolean;
    enableNumberedCitations: boolean;
  };
  researchId: ResearchId;
}) => {
  const [showThoughts, setShowThoughts] = React.useState(true);
  const hasThoughts = Boolean(props.message.thoughts);
  const hasValue = Boolean(props.message.value);
  const hasPointerCitations =
    (props.message.citations?.length ?? 0) > 0 &&
    (props.message.references?.length ?? 0) > 0;
  const isStreaming =
    props.message.status === "optimistic" ||
    props.message.status === "processing";

  const toggleThoughts = () => {
    setShowThoughts((show) => !show);
  };

  useEffect(() => {
    if (props.message.status === "completed" && hasValue) {
      setShowThoughts(false);
    }
  }, [props.message.status, hasValue]);

  const bubbleStatus =
    props.message.status === "completed" ? "completed" : "optimistic";

  const enableInsightsAgentFloatingCitationWidget = useFeatureFlagValue(
    "enableInsightsAgentFloatingCitationWidget",
  );

  const FlaggedCitationComponent: CitationComponent = useMemo(() => {
    return (_props) => (
      <Citation {..._props} numbered={props.flags.enableNumberedCitations} />
    );
  }, [props.flags.enableNumberedCitations]);

  const FloatingCitationComponent: CitationComponent = useMemo(() => {
    return (_props) => <FloatingCitation {..._props} loading={isStreaming} />;
  }, [isStreaming]);

  return (
    <MessageProvider researchId={props.researchId} messageId={props.message.id}>
      <MessageBubble
        key={props.message.id}
        message={{
          value: props.message.value,
          sender: props.message.sender,
          create_time: props.message.create_time,
          id: props.message.id,
          status: bubbleStatus,
        }}
      >
        {isStreaming &&
          !hasThoughts &&
          !hasValue &&
          props.message.status !== "completed" && (
            <span>
              Thinking <LoadingDots />
            </span>
          )}

        {hasThoughts && (
          <div className={styles.executionDetailsWrapper}>
            <ExecutionDetails
              open={showThoughts}
              onToggle={toggleThoughts}
              enableLimitedHeight={
                props.flags.limitedHeightExecutionDetailsFlag
              }
              autoScroll={isStreaming && !hasValue}
              title={
                isStreaming && !hasValue ? (
                  <>
                    Assistant steps <LoadingDots />
                  </>
                ) : (
                  "Assistant steps"
                )
              }
            >
              <div className={markdownStyles.MarkdownContent}>
                <Markdown remarkPlugins={[remarkGfm]}>
                  {props.message.thoughts || ""}
                </Markdown>
              </div>
            </ExecutionDetails>
          </div>
        )}

        {hasValue && hasPointerCitations && (
          <MarkdownWithCitations
            markdown={stripCiteTags(props.message.value || "")}
            citations={props.message.citations || []}
            references={props.message.references || []}
            citationComponent={
              enableInsightsAgentFloatingCitationWidget
                ? FloatingCitationComponent
                : FlaggedCitationComponent
            }
          />
        )}

        {hasValue && !hasPointerCitations && (
          <div className={markdownStyles.MarkdownContent}>
            <Markdown remarkPlugins={[remarkGfm]}>
              {stripCiteTags(props.message.value || "")}
            </Markdown>
          </div>
        )}
      </MessageBubble>
    </MessageProvider>
  );
};

const MessageProvider: React.FC<
  PropsWithChildren<{ researchId: ResearchId; messageId: string }>
> = ({ children, researchId, messageId }) => {
  const appService = useDeepResearchService();
  const enableRetryLastQuery = useFeatureFlagValue("enableRetryLastQuery");
  const isLastMessageFromAgent =
    appService.getLastMessageFromResearch(researchId, "agent")?.id ===
    messageId;
  const isLastMessageFromUser =
    appService.getLastMessageFromResearch(researchId, "user")?.id === messageId;
  const shouldDisplayRetryButton =
    (isLastMessageFromAgent || isLastMessageFromUser) && enableRetryLastQuery;

  const messageContextValue = React.useMemo(() => {
    return {
      resendLastQuery: shouldDisplayRetryButton
        ? () => appService.resendLastQueryStreaming({ researchId })
        : undefined,
    };
  }, [appService, researchId, shouldDisplayRetryButton]);

  return (
    <MessageContext.Provider value={messageContextValue}>
      {children}
    </MessageContext.Provider>
  );
};
